import {
  List,
  Datagrid,
  TextField,
  ReferenceField,
  DateField,
  NumberField,
  ReferenceInput,
  AutocompleteInput,
  SelectInput,
  TextInput,
  DateInput,
  TabbedShowLayout,
  useListFilterContext,
  useDataProvider,
} from 'react-admin'
import moment from 'moment/moment'

import ListStatusField from '../../components/ListStatusField'
import TextFieldTruncate from '../../components/TextFieldTruncate'
import FilterComponent from '../../components/Filter'
import { FILTER_DATE } from '../../components/constants'
import { exportExcel } from '../../utils/exportExcel'

const starChoices = [
  { id: 1, name: '1' },
  { id: 2, name: '2' },
  { id: 3, name: '3' },
  { id: 4, name: '4' },
  { id: 5, name: '5' },
]

const wordCountChoices = [
  { id: '0', name: '0 characters', min: 0, max: 0 },
  { id: '1-100', name: '1 - 100 characters', min: 1, max: 100 },
  { id: '101-200', name: '101 - 200 characters', min: 101, max: 200 },
  { id: 'gt200', name: '> 200 characters', min: 201 },
]

const BookCustomFilter = props => {
  const { filterValues } = useListFilterContext()
  return (
    <FilterComponent {...props}>
      <SelectInput
        label="Filter"
        source="date"
        choices={FILTER_DATE}
        alwaysOn
        allowEmpty={false}></SelectInput>
      {'custom' === filterValues.date && (
        <DateInput label="From date" source="fromDate" alwaysOn></DateInput>
      )}
      {'custom' === filterValues.date && (
        <DateInput label="To date" source="toDate" alwaysOn></DateInput>
      )}
      <ReferenceInput label="Book" source="entityId" reference="books" alwaysOn>
        <AutocompleteInput
          optionText="title"
          filterToQuery={searchText => ({
            title: { $iLike: `%${searchText}%` },
          })}
        />
      </ReferenceInput>
      <SelectInput
        source="bookContent"
        choices={starChoices}
        alwaysOn
        allowEmpty={true}></SelectInput>
      <SelectInput
        source="voice"
        choices={starChoices}
        alwaysOn
        allowEmpty={true}></SelectInput>
      <TextInput
        label="User ID"
        source="userId"
        alwaysOn
        type="number"
        allowEmpty={true}></TextInput>
      <SelectInput
        source="wordCountRange"
        label="Length (characters)"
        choices={wordCountChoices}
        alwaysOn
        allowEmpty={true}
      />
    </FilterComponent>
  )
}

const EnglishBookCustomFilter = props => {
  const { filterValues } = useListFilterContext()
  return (
    <FilterComponent {...props}>
      <SelectInput
        label="Filter"
        source="date"
        choices={FILTER_DATE}
        alwaysOn
        allowEmpty={false}></SelectInput>
      {'custom' === filterValues.date && (
        <DateInput label="From date" source="fromDate" alwaysOn></DateInput>
      )}
      {'custom' === filterValues.date && (
        <DateInput label="To date" source="toDate" alwaysOn></DateInput>
      )}
      <ReferenceInput
        label="English Book"
        source="entityId"
        reference="english-books"
        alwaysOn>
        <AutocompleteInput
          optionText="title"
          filterToQuery={searchText => ({
            title: { $iLike: `%${searchText}%` },
          })}
        />
      </ReferenceInput>
      <SelectInput
        source="bookContent"
        choices={starChoices}
        alwaysOn
        allowEmpty={true}></SelectInput>
      <SelectInput
        source="voice"
        choices={starChoices}
        alwaysOn
        allowEmpty={true}></SelectInput>
      <TextInput
        label="User ID"
        source="userId"
        alwaysOn
        type="number"
        allowEmpty={true}></TextInput>
      <SelectInput
        source="wordCountRange"
        label="Length (characters)"
        choices={wordCountChoices}
        alwaysOn
        allowEmpty={true}
      />
    </FilterComponent>
  )
}

const CourseCustomFilter = props => {
  const { filterValues } = useListFilterContext()

  return (
    <FilterComponent {...props}>
      <SelectInput
        label="Filter"
        source="date"
        choices={FILTER_DATE}
        alwaysOn
        allowEmpty={false}></SelectInput>
      {'custom' === filterValues.date && (
        <DateInput label="From date" source="fromDate" alwaysOn></DateInput>
      )}
      {'custom' === filterValues.date && (
        <DateInput label="To date" source="toDate" alwaysOn></DateInput>
      )}
      <ReferenceInput
        label="Course"
        source="entityId"
        reference="courses"
        alwaysOn>
        <AutocompleteInput
          optionText="title"
          filterToQuery={searchText => ({
            title: { $iLike: `%${searchText}%` },
          })}
        />
      </ReferenceInput>
      <SelectInput
        source="rating"
        choices={starChoices}
        alwaysOn
        allowEmpty={true}></SelectInput>
      <TextInput
        label="User ID"
        source="userId"
        alwaysOn
        type="number"
        allowEmpty={true}></TextInput>
      <SelectInput
        source="wordCountRange"
        label="Length (characters)"
        choices={wordCountChoices}
        alwaysOn
        allowEmpty={true}
      />
    </FilterComponent>
  )
}

const BookReviewList = props => {
  const dataProvider = useDataProvider()
  const exporter = async (records, fetchRelatedRecords) => {
    const { data } = await dataProvider.getList('reviews', {
      pagination: { page: 1, perPage: 5000 }, // adjust perPage if needed
      sort: { field: 'updatedAt', order: 'DESC' },
      filter: {
        entity: 'book',
      },
    })

    // Fetch related books in chunks of 500
    let allBooks = {}
    const chunkSize = 500
    if (data && data.length > 0) {
      const chunkPromises = []
      for (let i = 0; i < data.length; i += chunkSize) {
        const chunk = data.slice(i, i + chunkSize)
        chunkPromises.push(fetchRelatedRecords(chunk, 'entityId', 'books'))
      }
      const bookChunks = await Promise.all(chunkPromises)
      bookChunks.forEach(booksInChunk => {
        allBooks = { ...allBooks, ...booksInChunk }
      })
    }

    const exportData = data.map(record => {
      return {
        Id: record.id,
        'Book Id': record.entityId,
        'Book Title': allBooks[record.entityId]?.title,
        'Head Line': record.headLine,
        Review: record.review,
        bookContent: record.bookContent,
        rating: record.rating,
        voice: record.voice,
        'User Name': record.user && record.user.name,
        'User Id': record.userId,
        'Created at':
          record.createdAt && moment(record.createdAt).format('DD/MM/YYYY'),
        'Updated at':
          record.updatedAt && moment(record.updatedAt).format('DD/MM/YYYY'),
        status: record.status,
      }
    })

    exportExcel({
      data: exportData,
      title: 'Book reviews',
      type: 'xlsx',
    })
  }
  return (
    <List
      {...props}
      exporter={exporter}
      filters={<BookCustomFilter />}
      filterDefaultValues={{ entity: 'book' }}
      filter={{ entity: 'book' }}
      sort={{ field: 'updatedAt', order: 'DESC' }}>
      <Datagrid bulkActionButtons={false} rowClick={false}>
        <ReferenceField label="Book" source="entityId" reference="books">
          <TextField source="title" />
        </ReferenceField>
        <NumberField source="bookContent" />
        <NumberField source="voice" />
        <TextField source="headLine" />
        <ReferenceField label="User" source="userId" reference="user-details">
          <TextField source="id" />
        </ReferenceField>
        <TextFieldTruncate source="review" />
        <DateField source="createdAt" />
        <DateField source="updatedAt" />
        <ListStatusField resource="reviews" label="Status" />
      </Datagrid>
    </List>
  )
}

const EnglishBookReviewList = props => {
  const dataProvider = useDataProvider()
  const exporter = async (records, fetchRelatedRecords) => {
    const { data } = await dataProvider.getList('reviews', {
      pagination: { page: 1, perPage: 5000 }, // adjust perPage if needed
      sort: { field: 'updatedAt', order: 'DESC' },
      filter: {
        entity: 'english_book',
      },
    })

    const engBooks = await fetchRelatedRecords(
      data,
      'entityId',
      'english-books',
    )

    const exportData = data.map(record => {
      return {
        Id: record.id,
        'Book Id': record.entityId,
        'Book Title': engBooks[record.entityId]?.title,
        'Head Line': record.headLine,
        Review: record.review,
        bookContent: record.bookContent,
        rating: record.rating,
        voice: record.voice,
        'User Name': record.user && record.user.name,
        'User Id': record.userId,
        'Created at':
          record.createdAt && moment(record.createdAt).format('DD/MM/YYYY'),
        'Updated at':
          record.updatedAt && moment(record.updatedAt).format('DD/MM/YYYY'),
        status: record.status,
      }
    })

    exportExcel({
      data: exportData,
      title: 'English Book reviews',
      type: 'xlsx',
    })
  }

  return (
    <List
      {...props}
      exporter={exporter}
      filters={<EnglishBookCustomFilter />}
      filterDefaultValues={{ entity: 'english_book' }}
      filter={{ entity: 'english_book' }}
      sort={{ field: 'updatedAt', order: 'DESC' }}>
      <Datagrid bulkActionButtons={false} rowClick={false}>
        <ReferenceField
          label="English Book"
          source="entityId"
          reference="english-books">
          <TextField source="title" />
        </ReferenceField>
        <NumberField source="bookContent" />
        <NumberField source="voice" />
        <TextField source="headLine" />
        <ReferenceField label="User" source="userId" reference="user-details">
          <TextField source="id" />
        </ReferenceField>
        <TextFieldTruncate source="review" />
        <DateField source="createdAt" />
        <DateField source="updatedAt" />
        <ListStatusField resource="reviews" label="Status" />
      </Datagrid>
    </List>
  )
}

const CourseReviewList = props => {
  const dataProvider = useDataProvider()
  const exporter = async (records, fetchRelatedRecords) => {
    const { data } = await dataProvider.getList('reviews', {
      pagination: { page: 1, perPage: 5000 }, // adjust perPage if needed
      sort: { field: 'updatedAt', order: 'DESC' },
      filter: {
        entity: 'course',
      },
    })

    const courses = await fetchRelatedRecords(data, 'entityId', 'courses')

    const exportData = data.map(record => {
      return {
        Id: record.id,
        'PodCourse Id': record.entityId,
        'PodCourse Title': courses[record.entityId]?.title,
        rating: record.rating,
        'User Name': record.user && record.user.name,
        'User Id': record.userId,
        Positive: record.review,
        Negative: record.suggestion,
        'Created at':
          record.createdAt && moment(record.createdAt).format('DD/MM/YYYY'),
        'Updated at':
          record.updatedAt && moment(record.updatedAt).format('DD/MM/YYYY'),
        status: record.status,
      }
    })

    exportExcel({
      data: exportData,
      title: 'PodCourse reviews',
      type: 'xlsx',
    })
  }

  return (
    <List
      {...props}
      exporter={exporter}
      filters={<CourseCustomFilter />}
      filterDefaultValues={{ entity: 'course' }}
      filter={{ entity: 'course' }}
      sort={{ field: 'updatedAt', order: 'DESC' }}>
      <Datagrid bulkActionButtons={false} rowClick={false}>
        <ReferenceField label="Course" source="entityId" reference="courses">
          <TextField source="title" />
        </ReferenceField>
        <NumberField source="rating" />
        <ReferenceField label="User" source="userId" reference="user-details">
          <TextField source="id" />
        </ReferenceField>
        <TextFieldTruncate source="headLine" label={'Head Line'} />
        <TextFieldTruncate source="review" label={'Positive'} />
        <TextFieldTruncate source="suggestion" label={'Negative'} />
        <DateField source="createdAt" />
        <DateField source="updatedAt" />
        <ListStatusField resource="reviews" label="Status" />
      </Datagrid>
    </List>
  )
}

const ReviewList = () => (
  <List title="Reviews" pagination={null} exporter={false}>
    <TabbedShowLayout record={{ id: 1, title: 'Hello world' }}>
      <TabbedShowLayout.Tab label="Audiobook">
        <BookReviewList />
      </TabbedShowLayout.Tab>
      <TabbedShowLayout.Tab label="PodCourse">
        <CourseReviewList />
      </TabbedShowLayout.Tab>
      <TabbedShowLayout.Tab label="EnglishBook">
        <EnglishBookReviewList />
      </TabbedShowLayout.Tab>
    </TabbedShowLayout>
  </List>
)

export default ReviewList
